import { useIntl } from '@/intl'
import {
  Banner,
  Button,
  FormField,
  FormFieldControl,
  FormFieldError,
  FormFieldLabel,
  Icon,
  SpaceBetween,
  TextField,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'

import * as React from 'react'

import { styled } from 'styled-components'
import { GridTab } from '../Filters/GridTabs'
import { useGridTabsContext } from '../GridTabsContext'
import { useAlertService } from './hooks'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media ${tk('mq_tablet')} {
    flex-direction: row;
  }

  padding: ${tk('spacing_lg')};
  border: 2px solid #e7e0d7;
  border-radius: ${tk('radius_medium')};
  background-color: #f3f0ed;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-top: ${tk('spacing_sm')};

  @media ${tk('mq_tablet')} {
    margin-top: 0;
    max-width: 800px;
    margin-right: auto;
    margin-left: ${tk('spacing_md')};
  }
`

const EnvelopeIcon = styled(Icon)`
  color: #817564;
`

const TitleDescription = styled.div`
  display: flex;
  flex-direction: column;
`

const ConfirmButton = styled(Button)`
  @media ${tk('mq_tablet')} {
    width: fit-content;
  }
`

const EmailInput = styled(FormField)`
  max-width: 630px;
`

const Texts = styled(SpaceBetween)`
  flex: 1;
`

export function AlertService() {
  const intl = useIntl()
  const [email, setEmail] = React.useState('')
  const [isValidEmail, setIsValidEmail] = React.useState(true)
  const { mutate, isLoading, isError, isSuccess, reset: resetMutationState } = useAlertService()
  const { selectedTab } = useGridTabsContext()

  const checkEmailValidity = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/
    return emailRegex.test(value)
  }

  if (selectedTab !== 'alertService')
    return null

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handleConfirm = () => {
    const isValid = checkEmailValidity(email)
    if (isValid) {
      resetMutationState()
      mutate(email)
    }
    else {
      setIsValidEmail(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  const shouldShowInvalidEmailMessage = isError || !isValidEmail

  return (
    <Wrapper>
      <EnvelopeIcon glyph="IconMail" size="lg" />
      <Content>
        <SpaceBetween direction="y" spacing={tk('spacing_md')}>
          <TitleDescription>
            <Typography $variant="body-lg-bold">{intl.formatMessage({ id: 'price_alert_service-title' })}</Typography>
            <Typography $variant="body-md">{intl.formatMessage({ id: 'price_alert_service-description' })}</Typography>
          </TitleDescription>
          {isSuccess && (
            <Banner $type="success">
              <Texts direction="y">
                <Typography $variant="label">
                  {intl.formatMessage({ id: 'price_alert_service-success-title' })}
                </Typography>
                <Typography>{intl.formatMessage({ id: 'price_alert_service-success-description' })}</Typography>
              </Texts>
            </Banner>
          )}
          {!isSuccess && (
            <>
              <EmailInput $error={Boolean(email) && !isValidEmail}>
                <FormFieldLabel>{intl.formatMessage({ id: 'email_address' })}</FormFieldLabel>
                <FormFieldControl>
                  <TextField.Root disabled={isLoading}>
                    <TextField.Content>
                      <TextField.Input type="text" onChange={handleOnChange} value={email} onKeyDown={handleKeyDown} />
                    </TextField.Content>
                  </TextField.Root>
                </FormFieldControl>
                {shouldShowInvalidEmailMessage && (
                  <FormFieldError>{intl.formatMessage({ id: 'price_alert_service-error' })}</FormFieldError>
                )}
              </EmailInput>
              <Typography $variant="body-md">
                {intl.formatMessage({ id: 'price_alert_service-privacy-message' })}
              </Typography>
              <ConfirmButton onClick={handleConfirm} disabled={isLoading}>
                {intl.formatMessage({ id: 'price_alert_service-confirm' })}
              </ConfirmButton>
            </>
          )}
        </SpaceBetween>
      </Content>
    </Wrapper>
  )
}

export function AlertServiceTab() {
  const intl = useIntl()
  const { selectedTab, setSelectedTab } = useGridTabsContext()

  const handleClick = () => {
    setSelectedTab('alertService')
  }

  const nextYear = new Date().getFullYear() + 1

  return (
    <GridTab onClick={handleClick} $selected={selectedTab === 'alertService'}>
      <Typography $variant="label">
        {intl.formatMessage({ id: 'price_alert_service-next-summer' }, { year: nextYear })}
      </Typography>
    </GridTab>
  )
}
